/**
 * JunctionDiagram — 10 inline SVG diagrams for the Junctions Hub.
 *
 * Sister component to RoundaboutDiagram.jsx. Same visual language
 * (slate-700 road, amber-200 paint, emerald-500 accents), same
 * top-down stylised perspective, same DiagramFrame wrapper.
 *
 * Usage:
 *   <JunctionDiagram id="t_junction" className="w-full aspect-square" />
 */

const STROKE = "#94a3b8"; // slate-400
const ROAD = "#334155";   // slate-700
const PAINT = "#fde68a";  // amber-200 — painted markings
const ACCENT = "#10b981"; // emerald-500 — feature accent
const RED = "#ef4444";    // rose — danger signal
const AMBER = "#f59e0b";  // amber — caution / box

function ArrowMark({ x, y, rotate = 0, color = PAINT, scale = 1 }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`}>
      <path d="M0 -6 L4 4 L0 1 L-4 4 Z" fill={color} />
    </g>
  );
}

function GiveWayLine({ x1, y1, x2, y2 }) {
  // Two parallel dashed lines = give-way (UK).
  return (
    <>
      <line x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={PAINT} strokeWidth="1.5" strokeDasharray="3 2" />
      <line x1={x1 + 3} y1={y1 + 3} x2={x2 + 3} y2={y2 + 3}
            stroke={PAINT} strokeWidth="1.5" strokeDasharray="3 2" />
    </>
  );
}

function DiagramFrame({ children, label }) {
  return (
    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"
         className="w-full h-full">
      <rect width="200" height="200" fill="#0f172a" rx="12" />
      <defs>
        <pattern id="j-grid" width="20" height="20"
                 patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none"
                stroke="#1e293b" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="200" height="200" fill="url(#j-grid)" rx="12" />
      {children}
      {label && (
        <text x="100" y="190" textAnchor="middle"
              fill={STROKE} fontSize="9" fontFamily="ui-sans-serif">
          {label}
        </text>
      )}
    </svg>
  );
}

// ═══════ T-JUNCTION ═══════
function TJunctionDiagram() {
  return (
    <DiagramFrame label="T-junction">
      {/* Major road (horizontal) + minor road (vertical) */}
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <rect x="80" y="80" width="40" height="120" fill={ROAD} />
      {/* Centre line on major road */}
      <line x1="0" y1="100" x2="80" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="120" y1="100" x2="200" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      {/* Give-way line on the minor approach */}
      <GiveWayLine x1="82" y1="118" x2="118" y2="118" />
      {/* Arrows */}
      <ArrowMark x="40" y="100" rotate={-90} />
      <ArrowMark x="160" y="100" rotate={-90} />
      <ArrowMark x="100" y="180" rotate={180} />
    </DiagramFrame>
  );
}

// ═══════ CROSSROADS ═══════
function CrossroadsDiagram() {
  return (
    <DiagramFrame label="Crossroads">
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <rect x="80" y="0" width="40" height="200" fill={ROAD} />
      {/* Centre lines */}
      <line x1="0" y1="100" x2="80" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="120" y1="100" x2="200" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="100" y1="0" x2="100" y2="80"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="100" y1="120" x2="100" y2="200"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      {/* Two give-way lines on the minor (vertical) approaches */}
      <GiveWayLine x1="82" y1="78"  x2="118" y2="78" />
      <GiveWayLine x1="82" y1="122" x2="118" y2="122" />
      <ArrowMark x="40" y="100" rotate={-90} />
      <ArrowMark x="160" y="100" rotate={-90} />
    </DiagramFrame>
  );
}

// ═══════ OPEN CROSSROADS ═══════
function OpenCrossroadsDiagram() {
  return (
    <DiagramFrame label="Open crossroads">
      {/* Wider roads + clear sightlines (no buildings) */}
      <rect x="0" y="70" width="200" height="60" fill={ROAD} />
      <rect x="70" y="0" width="60" height="200" fill={ROAD} />
      <line x1="0" y1="100" x2="70" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="130" y1="100" x2="200" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="100" y1="0" x2="100" y2="70"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="100" y1="130" x2="100" y2="200"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      {/* Sightline indicators — long visibility lines */}
      <line x1="20" y1="60"  x2="180" y2="60"
            stroke={ACCENT} strokeWidth="0.5" strokeDasharray="2 3" opacity="0.5" />
      <line x1="20" y1="140" x2="180" y2="140"
            stroke={ACCENT} strokeWidth="0.5" strokeDasharray="2 3" opacity="0.5" />
      <ArrowMark x="40" y="100" rotate={-90} />
      <ArrowMark x="160" y="100" rotate={-90} />
    </DiagramFrame>
  );
}

// ═══════ STAGGERED ═══════
function StaggeredDiagram() {
  return (
    <DiagramFrame label="Staggered">
      {/* Major road */}
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      {/* Two side roads offset — left side bottom, right side top */}
      <rect x="50" y="120" width="40" height="80" fill={ROAD} />
      <rect x="120" y="0" width="40" height="80" fill={ROAD} />
      {/* Centre line on major */}
      <line x1="0" y1="100" x2="50" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="90" y1="100" x2="120" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="160" y1="100" x2="200" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      {/* Two give-way lines */}
      <GiveWayLine x1="52" y1="122" x2="88"  y2="122" />
      <GiveWayLine x1="122" y1="78" x2="158" y2="78" />
      <ArrowMark x="30" y="100" rotate={-90} />
      <ArrowMark x="180" y="100" rotate={-90} />
    </DiagramFrame>
  );
}

// ═══════ BOX JUNCTION ═══════
function BoxJunctionDiagram() {
  return (
    <DiagramFrame label="Box junction">
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <rect x="80" y="0" width="40" height="200" fill={ROAD} />
      {/* Yellow box hatching */}
      <g stroke={AMBER} strokeWidth="0.8" fill="none">
        <rect x="80" y="80" width="40" height="40" fill="#3f3014" opacity="0.6" />
        {[...Array(7).keys()].map((i) => (
          <line key={i}
                x1={80} y1={80 + i * 6}
                x2={80 + i * 6} y2={80} />
        ))}
        {[...Array(7).keys()].map((i) => (
          <line key={`b${i}`}
                x1={80 + i * 6} y1={120}
                x2={120} y2={120 - i * 6} />
        ))}
      </g>
      <ArrowMark x="40" y="100" rotate={-90} />
      <ArrowMark x="160" y="100" rotate={-90} />
    </DiagramFrame>
  );
}

// ═══════ TRAFFIC LIGHT ═══════
function TrafficLightDiagram() {
  return (
    <DiagramFrame label="Traffic-light">
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <rect x="80" y="0" width="40" height="200" fill={ROAD} />
      {/* Signal poles at corners */}
      <g>
        <rect x="64" y="64" width="6" height="14" fill="#1f2937" rx="1" />
        <circle cx="67" cy="68" r="1.5" fill={RED} />
        <circle cx="67" cy="71" r="1.5" fill={AMBER} />
        <circle cx="67" cy="74" r="1.5" fill={ACCENT} />
      </g>
      <g>
        <rect x="130" y="122" width="6" height="14" fill="#1f2937" rx="1" />
        <circle cx="133" cy="126" r="1.5" fill={RED} />
        <circle cx="133" cy="129" r="1.5" fill={AMBER} />
        <circle cx="133" cy="132" r="1.5" fill={ACCENT} />
      </g>
      {/* Stop lines (solid) */}
      <line x1="0"  y1="80"  x2="80"  y2="80"  stroke="white" strokeWidth="2" />
      <line x1="120" y1="120" x2="200" y2="120" stroke="white" strokeWidth="2" />
      <ArrowMark x="40" y="100" rotate={-90} />
      <ArrowMark x="160" y="100" rotate={-90} />
    </DiagramFrame>
  );
}

// ═══════ SLIP ROAD ═══════
function SlipRoadDiagram() {
  return (
    <DiagramFrame label="Slip road">
      {/* Main fast road */}
      <rect x="0" y="60" width="200" height="50" fill={ROAD} />
      <line x1="0" y1="85" x2="200" y2="85"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      {/* Slip road curving in from bottom-left */}
      <path d="M 0 200 Q 60 200 100 150 L 200 110 L 200 130 L 100 175 Q 60 220 0 220 Z"
            fill={ROAD} />
      {/* Acceleration broken line */}
      <line x1="100" y1="135" x2="200" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <ArrowMark x="40" y="85" rotate={-90} color={PAINT} />
      <ArrowMark x="180" y="85" rotate={-90} color={PAINT} />
      <ArrowMark x="60" y="180" rotate={-50} color={ACCENT} />
    </DiagramFrame>
  );
}

// ═══════ MERGE LANE ═══════
function MergeLaneDiagram() {
  return (
    <DiagramFrame label="Merge lane">
      {/* Two lanes becoming one — taper from right */}
      <path d="M 0 60 L 200 60 L 200 110 L 100 110 L 0 140 Z" fill={ROAD} />
      <line x1="0" y1="100" x2="100" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      {/* Diagonal taper line */}
      <line x1="100" y1="110" x2="0" y2="140"
            stroke={PAINT} strokeWidth="1.5" />
      <ArrowMark x="160" y="85" rotate={-90} color={PAINT} />
      <ArrowMark x="160" y="125" rotate={-90} color={ACCENT} />
      <text x="100" y="170" textAnchor="middle" fill={ACCENT}
            fontSize="8" fontFamily="ui-sans-serif">
        Merge in turn
      </text>
    </DiagramFrame>
  );
}

// ═══════ GIVE WAY ═══════
function GiveWayDiagram() {
  return (
    <DiagramFrame label="Give way">
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <rect x="80" y="80" width="40" height="120" fill={ROAD} />
      <line x1="0" y1="100" x2="80" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="120" y1="100" x2="200" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      {/* Dashed give-way line (NOT solid stop) */}
      <GiveWayLine x1="82" y1="118" x2="118" y2="118" />
      {/* Triangular give-way sign */}
      <g transform="translate(140 130)">
        <polygon points="0,-14 12,8 -12,8" fill="white" stroke={RED} strokeWidth="2" />
      </g>
      <ArrowMark x="40" y="100" rotate={-90} />
      <ArrowMark x="100" y="180" rotate={180} />
    </DiagramFrame>
  );
}

// ═══════ CLOSED / BLIND ═══════
function ClosedBlindDiagram() {
  return (
    <DiagramFrame label="Closed / blind">
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <rect x="80" y="80" width="40" height="120" fill={ROAD} />
      <line x1="0" y1="100" x2="80" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="120" y1="100" x2="200" y2="100"
            stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <GiveWayLine x1="82" y1="118" x2="118" y2="118" />
      {/* Walls/hedges blocking view */}
      <rect x="20" y="40" width="50" height="35" fill="#1e293b" stroke={ACCENT} strokeWidth="0.5" />
      <rect x="130" y="40" width="50" height="35" fill="#1e293b" stroke={ACCENT} strokeWidth="0.5" />
      {/* Hatched "blocked view" texture */}
      <g stroke={STROKE} strokeWidth="0.4" opacity="0.5">
        {[...Array(6).keys()].map((i) => (
          <line key={`l${i}`} x1={20 + i * 8} y1={40} x2={20} y2={40 + i * 6} />
        ))}
        {[...Array(6).keys()].map((i) => (
          <line key={`r${i}`} x1={130 + i * 8} y1={40} x2={130} y2={40 + i * 6} />
        ))}
      </g>
      <ArrowMark x="100" y="180" rotate={180} />
    </DiagramFrame>
  );
}

const DIAGRAMS = {
  t_junction:       TJunctionDiagram,
  crossroads:       CrossroadsDiagram,
  open_crossroads:  OpenCrossroadsDiagram,
  staggered:        StaggeredDiagram,
  box_junction:     BoxJunctionDiagram,
  traffic_light:    TrafficLightDiagram,
  slip_road:        SlipRoadDiagram,
  merge_lane:       MergeLaneDiagram,
  give_way:         GiveWayDiagram,
  closed_blind:     ClosedBlindDiagram,
};

export default function JunctionDiagram({ id, className = "" }) {
  const Cmp = DIAGRAMS[id] || TJunctionDiagram;
  return (
    <div className={className} data-testid={`junction-diagram-${id}`}>
      <Cmp />
    </div>
  );
}
